/**
 * One inbound van as a depot manager reads it on a phone: registration and arrival first, then a
 * line per shipper.
 */
import React from "react"

import { format } from "date-fns"
import { AlertTriangle, ExternalLink, MapPin, RotateCcw, TrendingUp } from "lucide-react"

import { Badge } from "@/components/ui/badge"
import { getSamsaraRouteUrl } from "@/config/constants"
import { displayDepot } from "@/lib/shiftCover"
import { toneClasses } from "@/lib/tone"
import { ARRIVAL_LABELS, arrivalOf } from "@/lib/turnaroundArrival"
import { collectionFigure } from "@/lib/turnaroundCollections"
import { cn } from "@/lib/utils"
import { VOLUME_SOURCE_LABELS } from "@/lib/volumeReporting"
import { TurnaroundRoute } from "@/types/collections"

import { getDelayColor, getStatusColor, getStatusIcon } from "./turnaroundStatus"

interface TurnaroundArrivalCardProps {
  turnaround: TurnaroundRoute
  showDepot: boolean
}

const TurnaroundArrivalCard: React.FC<TurnaroundArrivalCardProps> = ({
  turnaround,
  showDepot,
}) => {
  const arrival = arrivalOf(turnaround)
  const collections = turnaround.collections ?? []
  const delay = turnaround.arrivalDelayMinutes

  /**
   * A route whose collections all came back empty has a real zero; a leg with no collections has
   * nothing.
   */
  const hasVolumeToReport = turnaround.volumeReported > 0 || collections.length > 0

  /* The reg carries the Samsara link because it's what the yard recognises; the route code sits
     below. */
  const header = (
    <div className="flex min-h-14 items-center justify-between gap-3 px-4 py-3">
      <div className="min-w-0">
        <div className="flex items-center gap-1.5">
          <span
            className={cn(
              "truncate text-base font-semibold",
              turnaround.vehicleReg === "Unknown"
                ? "italic text-palette-jungleGreen400"
                : "text-palette-jungleGreen900"
            )}
          >
            {turnaround.vehicleReg}
          </span>
          {turnaround.samsaraRouteId && (
            <ExternalLink
              size={12}
              className="flex-shrink-0 text-palette-jungleGreen400"
              aria-hidden="true"
            />
          )}
        </div>
        <div className="mt-0.5 text-[11px] uppercase tracking-wide text-palette-jungleGreen400">
          {ARRIVAL_LABELS[arrival.kind]}
          {arrival.kind !== "planned" && (
            <> · Planned {format(turnaround.plannedArrival, "HH:mm")}</>
          )}
        </div>
      </div>

      <div className="flex flex-shrink-0 flex-col items-end">
        <span className="font-mono text-2xl font-bold leading-none tracking-tight text-palette-jungleGreen900">
          {format(arrival.at, "HH:mm")}
        </span>
        {delay !== undefined && delay !== 0 && (
          <span
            className={cn(
              "mt-1 flex items-center gap-1 text-xs font-medium",
              getDelayColor(delay)
            )}
          >
            {delay > 0 ? <AlertTriangle size={10} /> : <TrendingUp size={10} />}
            {delay > 0 ? "+" : ""}
            {delay} min
          </span>
        )}
      </div>
    </div>
  )

  return (
    <div className="overflow-hidden rounded-lg border border-palette-jungleGreen200 bg-white">
      {turnaround.samsaraRouteId ? (
        <a
          href={getSamsaraRouteUrl(turnaround.samsaraRouteId)}
          target="_blank"
          rel="noopener noreferrer"
          title="Open this route in Samsara"
          className="block no-underline active:bg-palette-jungleGreen000"
        >
          {header}
        </a>
      ) : (
        header
      )}

      <div className="flex flex-wrap items-center gap-x-2 gap-y-1 border-t border-palette-jungleGreen100 px-4 py-2">
        <Badge
          variant="outline"
          className={cn("flex items-center gap-1 capitalize", getStatusColor(turnaround.status))}
        >
          {getStatusIcon(turnaround.status, 11)}
          <span className="text-[11px]">{turnaround.status.replace("_", " ")}</span>
        </Badge>
        {showDepot && turnaround.depot && (
          <span className="text-xs text-palette-jungleGreen600">
            {displayDepot(turnaround.depot) ?? turnaround.depot}
          </span>
        )}
        <span className="truncate font-mono text-[10px] text-palette-jungleGreen400">
          {turnaround.routeCode}
        </span>
      </div>

      <div className="border-t border-palette-jungleGreen100">
        {collections.length === 0 ? (
          <div className="px-4 py-3 text-sm italic text-palette-jungleGreen400">
            No collections on this leg
          </div>
        ) : (
          <ul className="divide-y divide-palette-jungleGreen100">
            {collections.map((collection) => (
              <li
                key={collection.stopInstanceId}
                className="flex items-baseline justify-between gap-3 px-4 py-2"
              >
                <span className="min-w-0 flex-1 break-words text-sm text-palette-jungleGreen800">
                  {collection.name}
                </span>
                <span className="flex flex-shrink-0 items-baseline gap-1.5">
                  <span
                    className={cn(
                      "text-sm tabular-nums",
                      collection.quantity === null
                        ? "italic text-palette-jungleGreen400"
                        : "font-medium text-palette-jungleGreen900"
                    )}
                  >
                    {collectionFigure(collection)}
                  </span>
                  {collection.quantity !== null && !collection.isActual && (
                    <span className="text-[10px] text-palette-jungleGreen400">forecast</span>
                  )}
                </span>
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* Same figure and wording as the desktop volume column, so phone and wall display agree. */}
      <div className="flex items-center justify-between gap-3 border-t border-palette-jungleGreen100 bg-palette-jungleGreen000 px-4 py-2">
        {hasVolumeToReport ? (
          <div className="min-w-0">
            <div className="text-sm font-medium tabular-nums text-palette-jungleGreen900">
              {turnaround.volumeReported.toLocaleString()} parcels
              {turnaround.loadingUnitsReported != null && turnaround.loadingUnitsReported > 0 && (
                <span className="font-normal text-palette-jungleGreen600">
                  {" · "}
                  {turnaround.loadingUnitsReported.toLocaleString()}{" "}
                  {(turnaround.loadingUnitType || "units").toLowerCase()}
                </span>
              )}
            </div>
            <div
              className={cn(
                "text-[11px]",
                turnaround.volumeSource === "actual"
                  ? toneClasses("success").text
                  : turnaround.volumeSource === "partial"
                    ? toneClasses("info").text
                    : "text-palette-jungleGreen400"
              )}
            >
              {VOLUME_SOURCE_LABELS[turnaround.volumeSource]}
              {turnaround.volumeSource === "partial" &&
                ` ${turnaround.stopsConfirmed}/${turnaround.stopsCarryingVolume}`}
            </div>
          </div>
        ) : (
          <div />
        )}

        <div className="flex-shrink-0 text-right">
          {turnaround.hasTurnaround && turnaround.nextDeparture ? (
            <>
              <div className="flex items-center justify-end gap-1 text-sm font-medium text-palette-jungleGreen900">
                <RotateCcw size={11} className="text-cyan-600" />
                {format(turnaround.nextDeparture, "HH:mm")}
              </div>
              {turnaround.nextDestination && (
                <div className="flex items-center justify-end gap-1 text-[11px] text-amber-600">
                  <MapPin size={9} />
                  <span className="max-w-[9rem] truncate">{turnaround.nextDestination}</span>
                </div>
              )}
            </>
          ) : (
            <span className="text-[11px] text-palette-jungleGreen400">Terminates</span>
          )}
        </div>
      </div>
    </div>
  )
}

export default TurnaroundArrivalCard
